import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  path?: string;
  image?: string;
  type?: 'website' | 'article';
  noIndex?: boolean;
}

const BASE_URL = 'https://govtoolspro.com';
const DEFAULT_IMAGE = `${BASE_URL}/logo512.png`;
const SITE_NAME = 'GovToolsPro';

export function SEOHead({
  title,
  description,
  path = '',
  image = DEFAULT_IMAGE,
  type = 'website',
  noIndex = false,
}: SEOHeadProps) {
  const fullTitle = title === SITE_NAME ? title : `${title} | ${SITE_NAME}`;
  const canonicalUrl = `${BASE_URL}${path}`;
  const imageUrl = image.startsWith('http') ? image : `${BASE_URL}${image}`;

  useEffect(() => {
    document.title = fullTitle;

    const setMeta = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const setMetaProperty = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const setLink = (rel: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        document.head.appendChild(link);
      }
      link.href = href;
    };

    setMeta('description', description);

    setMetaProperty('og:title', fullTitle);
    setMetaProperty('og:description', description);
    setMetaProperty('og:image', imageUrl);
    setMetaProperty('og:url', canonicalUrl);
    setMetaProperty('og:type', type);
    setMetaProperty('og:site_name', SITE_NAME);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);
    setMeta('twitter:image', imageUrl);

    setLink('canonical', canonicalUrl);

    if (noIndex) {
      setMeta('robots', 'noindex, nofollow');
    } else {
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta) {
        robotsMeta.remove();
      }
    }

    return () => {
      document.title = SITE_NAME;
    };
  }, [fullTitle, description, canonicalUrl, imageUrl, type, noIndex]);

  return null;
}

export const SEO_PAGES = {
  home: {
    title: 'GovToolsPro - AI-Powered SAM.gov Contract Analysis',
    description: 'Find and win government contracts with AI. Get instant bid estimates, competitor intelligence, and market insights directly on SAM.gov. Free Chrome extension.',
    path: '/',
  },
  about: {
    title: 'About GovToolsPro - Government Contract Intelligence',
    description: 'Learn how GovToolsPro helps contractors analyze SAM.gov opportunities, research competitors, and estimate bids using AI and historical award data.',
    path: '/about',
  },
  services: {
    title: 'Features & Services - GovToolsPro',
    description: 'Discover GovToolsPro features: bid estimation, competitor research, document analysis, award trends, and geographic spending data for government contracts.',
    path: '/services',
  },
  plans: {
    title: 'Pricing - Free Government Contract Tools | GovToolsPro',
    description: 'GovToolsPro is free to use. Get instant bid estimates, competitor research, and market insights. Buy credits only for AI document analysis when you need it.',
    path: '/plans',
  },
  faq: {
    title: 'FAQ - GovToolsPro Government Contract Tools',
    description: 'Get answers about GovToolsPro Chrome extension, SAM.gov analysis, bid estimation, competitor research, and government contract intelligence features.',
    path: '/faq',
  },
  support: {
    title: 'Support - GovToolsPro',
    description: 'Get help with GovToolsPro Chrome extension. Contact support for questions about SAM.gov analysis, bid estimation, and government contract research.',
    path: '/support',
  },
  privacy: {
    title: 'Privacy Policy - GovToolsPro',
    description: 'GovToolsPro privacy policy. Learn how we protect your data when using our SAM.gov contract analysis Chrome extension.',
    path: '/privacy',
  },
  terms: {
    title: 'Terms of Service - GovToolsPro',
    description: 'GovToolsPro terms of service. Understand the terms for using our SAM.gov contract analysis tools and Chrome extension.',
    path: '/terms',
  },
  login: {
    title: 'Login - GovToolsPro',
    description: 'Sign in to GovToolsPro to access your saved solicitations, bid estimates, and government contract analysis tools.',
    path: '/login',
    noIndex: true,
  },
  signup: {
    title: 'Sign Up - GovToolsPro',
    description: 'Create a free GovToolsPro account. Get started with AI-powered SAM.gov contract analysis, bid estimation, and competitor research.',
    path: '/signup',
    noIndex: true,
  },
  dashboard: {
    title: 'Dashboard - GovToolsPro',
    description: 'Your GovToolsPro dashboard. View saved solicitations, recent analyses, and manage your government contracting activities.',
    path: '/dashboard',
    noIndex: true,
  },
} as const;
